import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import emailjs from '@emailjs/browser';
import './ApplicationForm.css';

interface ApplicationFormProps {
  jobId: string;
  jobTitle: string;
  uploaderEmail: string;
  language: 'nl' | 'en';
  onClose: () => void;
  onSuccess: () => void;
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({
  jobId,
  jobTitle,
  uploaderEmail,
  language,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cvFile, setCvFile] = useState<File | null>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    coverLetter: '',
    linkedinUrl: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (file.type !== 'application/pdf') {
        setError(language === 'nl' ? 'Upload alleen PDF bestanden' : 'Please upload PDF files only');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(language === 'nl' ? 'Bestand is te groot (max 5MB)' : 'File is too large (max 5MB)');
        return;
      }
      setCvFile(file);
      setError(null);
    }
  };

  const uploadCV = async (file: File): Promise<string> => {
    if (!supabase) throw new Error('Supabase not configured');

    const fileExt = 'pdf';
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `cvs/${fileName}`;

    const { error } = await supabase.storage
      .from('applications')
      .upload(filePath, file, {
        cacheControl: '3600', // Cache for 1 hour to reduce egress
        upsert: false
      });

    if (error) throw error;

    // Get public URL (bucket is public)
    const { data: { publicUrl } } = supabase.storage
      .from('applications')
      .getPublicUrl(filePath);

    return publicUrl;
  };

  const sendEmailNotification = async (cvUrl: string) => {
    try {
      const templateParams = {
        to_email: uploaderEmail,
        job_title: jobTitle,
        first_name: formData.firstName,
        last_name: formData.lastName,
        applicant_email: formData.email,
        phone: formData.phone,
        linkedin_url: formData.linkedinUrl || 'Not provided',
        cover_letter: formData.coverLetter || 'No cover letter provided',
        cv_url: cvUrl,
      };

      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_APPLICATION_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        console.warn('EmailJS not configured properly');
        return;
      }

      await emailjs.send(serviceId, templateId, templateParams, publicKey);
    } catch (error) {
      console.error('Error sending email:', error);
      // Don't throw - email is optional, application should still be saved
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!cvFile) {
      setError(language === 'nl' ? 'Upload je CV (PDF)' : 'Please upload your CV (PDF)');
      return;
    }

    setLoading(true);

    try {
      // Upload CV
      const cvUrl = await uploadCV(cvFile);

      // Save application to database
      if (!supabase) throw new Error('Supabase not configured');

      const { error: dbError } = await supabase.from('applications').insert({
        job_id: jobId,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        cv_url: cvUrl,
        cover_letter: formData.coverLetter || null,
        linkedin_url: formData.linkedinUrl || null,
        status: 'new',
      });

      if (dbError) throw dbError;

      // Send email notification
      await sendEmailNotification(cvUrl);

      // Success!
      onSuccess();
    } catch (err: any) {
      console.error('Application error:', err);
      setError(err.message || (language === 'nl' ? 'Er is iets misgegaan' : 'Something went wrong'));
      setLoading(false);
    }
  };

  const texts = {
    nl: {
      title: 'Solliciteer nu',
      firstName: 'Voornaam',
      lastName: 'Achternaam',
      email: 'E-mail',
      phone: 'Telefoonnummer',
      cv: 'CV (PDF)',
      coverLetter: 'Motivatiebrief (optioneel)',
      linkedin: 'LinkedIn profiel (optioneel)',
      submit: 'Verstuur sollicitatie',
      cancel: 'Annuleren',
      submitting: 'Versturen...',
    },
    en: {
      title: 'Apply Now',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone Number',
      cv: 'CV (PDF)',
      coverLetter: 'Cover Letter (optional)',
      linkedin: 'LinkedIn profile (optional)',
      submit: 'Submit Application',
      cancel: 'Cancel',
      submitting: 'Submitting...',
    },
  };

  const t = texts[language];

  return (
    <div className="application-form-overlay" onClick={onClose}>
      <div className="application-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="application-form-header">
          <h2>{t.title}</h2>
          <button onClick={onClose} className="application-form-close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18" strokeWidth="2"/>
              <line x1="6" y1="6" x2="18" y2="18" strokeWidth="2"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="application-form">
          {error && (
            <div className="application-form-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2"/>
                <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2"/>
              </svg>
              {error}
            </div>
          )}

          <div className="application-form-grid">
            <div className="application-form-group">
              <label htmlFor="firstName">{t.firstName} *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="application-form-group">
              <label htmlFor="lastName">{t.lastName} *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="application-form-group">
              <label htmlFor="email">{t.email} *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="application-form-group">
              <label htmlFor="phone">{t.phone} *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="application-form-group full-width">
              <label htmlFor="cv">{t.cv} *</label>
              <input
                type="file"
                id="cv"
                accept=".pdf"
                onChange={handleFileChange}
                required
              />
              {cvFile && (
                <div className="file-selected">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  {cvFile.name}
                </div>
              )}
            </div>

            <div className="application-form-group full-width">
              <label htmlFor="linkedinUrl">{t.linkedin}</label>
              <input
                type="url"
                id="linkedinUrl"
                name="linkedinUrl"
                value={formData.linkedinUrl}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/..."
              />
            </div>

            <div className="application-form-group full-width">
              <label htmlFor="coverLetter">{t.coverLetter}</label>
              <textarea
                id="coverLetter"
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleChange}
                rows={6}
              />
            </div>
          </div>

          <div className="application-form-actions">
            <button type="button" onClick={onClose} className="application-form-btn-cancel">
              {t.cancel}
            </button>
            <button type="submit" className="application-form-btn-submit" disabled={loading}>
              {loading ? t.submitting : t.submit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplicationForm;
